/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Appliance3d, Appliance3dController } from '@rta/rta-blueprint-3d-component';
import { Box, Checkbox, Flex, Stack, Text, useColorModeValue, Image, Center } from '@chakra-ui/react';
import { Components, getComponentImageFileCfn, PanelItem, useSubscription } from '@rta/rta-blueprint-library';

import { ContextPanelContent } from './context-panel-content';
import { DesignState } from '@context';
import { lang } from '@lang';
import { useDesign } from '@hooks';
import { InlineNumberInput, InlineSlider } from './controls';
import { useEffect, useRef, useState, type JSX } from 'react';
import { isWebGLAvailable } from '../../../utils/webgl-check';
import { SimpleErrorBoundary } from '../../ui/simple-error-boundary';

export type AppliancePropertiesPanelProps = {
	id: string | null;
};

export const AppliancePropertiesPanel = ({ id }: AppliancePropertiesPanelProps): JSX.Element => {
	// const dispatch = useDispatch();
	const [controller] = useDesign() as [BpController | null, DesignState | null];
	const descriptionColor = useColorModeValue('gray.500', 'gray.300');
	const sectionHeaderColor = useColorModeValue('gray.600', 'gray.300');
	const dimensionHeaderColor = useColorModeValue('gray.600', 'gray.300');
	const appliance3dController = useRef<Appliance3dController>(new Appliance3dController()).current;
	const [panelItem, setPanelItem] = useState<PanelItem | null>(null);
	const [, setLandingZoneTick] = useState(0);

	const kitchen = controller?.kitchen ?? null;
	const components: Components | null = kitchen?.context.components ?? null;
	// Which axis the dimension offsets edit. A panel opened from the 3D view gets the 3D controller,
	// which has no 2D navigation, so it reads the item's own placement instead.
	const isCountertop = controller?.navigation
		? controller.navigation.panelId === null
		: panelItem?.item.component.placement === Components.PLACEMENT_TOP_ONLY;
	const readonly = controller?.readonly ?? true;

	const skuCurrentItem = panelItem ? components?.findId(panelItem.item.id) ?? null : null;

	useSubscription(controller, false, [['navigation-panel', 'updated']]);

	useEffect(() => {
		if (controller) {
			const locator = id ? kitchen?.getReferenceMap(id) ?? null : null;
			const pi: PanelItem | null = locator?.panelItem ?? null;

			appliance3dController.component = pi?.item.component ?? null;

			setPanelItem(pi);
		}
	}, [controller, id]);

	if (!controller || skuCurrentItem === null || panelItem === null) return <></>;

	const isCircularCutout = skuCurrentItem.category?.key === 'hole';

	return <ContextPanelContent title={lang.contextPanels.title.applianceProperties}>
		<Stack pt="10px" spacing="0">
			<Text ml="5px" fontSize="md" fontWeight="medium">{skuCurrentItem.sku}</Text>
			<Box marginBottom="10px" p="5px" width="100%" bgColor="gray.100" borderColor="gray.200" border="1px" rounded="10px" overflow="hidden">
				{ /* Tier 1: SVG (if available) - preferred method */ }
				{ skuCurrentItem.svg?.item
					? <svg
						height="150px"
						viewBox={`0 0 ${skuCurrentItem.size.width} ${skuCurrentItem.size.height}`}
					>{ skuCurrentItem.svg.item({ x: 0, y: 0, diameter: skuCurrentItem.cutout.width })}</svg>
					: /* Tier 2: 3D view with WebGL check and error boundary fallback to image */
					  isWebGLAvailable() && appliance3dController
						? <SimpleErrorBoundary
							fallback={
								<Center h="150px">
									<Image
										objectFit="contain"
										objectPosition="center"
										src={`${import.meta.env.VITE_ASSETS_BASE_URL}/products/${getComponentImageFileCfn(skuCurrentItem)}`}
										fallbackSrc={`${import.meta.env.VITE_ASSETS_BASE_URL}/products/placeholder-appliance.png`}
										alt={`${skuCurrentItem.sku} - ${skuCurrentItem.description}`}
										width="250px"
										height="150px"
									/>
								</Center>
							}
						>
							<Box w={'100%'} h={'150px'} overflow="hidden" bgColor="gray.100">
								<Appliance3d controller={appliance3dController} backgroundColor="#edf2f7" />
							</Box>
						</SimpleErrorBoundary>
						: /* Tier 3: Static image fallback when WebGL not available */
						<Center h="150px">
							<Image
								objectFit="contain"
								objectPosition="center"
								src={`${import.meta.env.VITE_ASSETS_BASE_URL}/products/${getComponentImageFileCfn(skuCurrentItem)}`}
								fallbackSrc={`${import.meta.env.VITE_ASSETS_BASE_URL}/products/placeholder-appliance.png`}
								alt={`${skuCurrentItem.sku} - ${skuCurrentItem.description}`}
								width="250px"
								height="150px"
							/>
						</Center>
				}
			</Box>

			<Flex pl="5px" pb="10px" justifyContent="space-between">
				<Text color={descriptionColor} fontSize="sm">{skuCurrentItem.description}</Text>
			</Flex>

			<Box pl="5px" pb="8px" textAlign="left">
				<Checkbox
					size="sm"
					isChecked={panelItem.item.landingZone}
					isDisabled={readonly}
					onChange={(e) => {
						panelItem.item.landingZoneOverride = e.target.checked;
						setLandingZoneTick((t) => t + 1);
					}}
				>
					<Text fontSize="small">Landing zone</Text>
				</Checkbox>
				<Text fontSize="10px" color="gray.500" pl="24px">
					catalog default: {panelItem.item.component?.landingZone ? 'on' : 'off'}
					{panelItem.item.landingZoneOverride !== null ? ' — overridden here' : ''}
				</Text>
			</Box>

			<Text pl="5px" color={sectionHeaderColor} fontSize="medium"
				fontWeight="medium"
			>Dimensional Details</Text>
			<Box marginBottom={3} padding={2} textAlign="left"
				borderLeft="1px solid grey" borderTop="1px" roundedTopLeft="7px"
			>
				<Stack spacing="1" width="100%" height="65%">
					{/* Circular cutouts show editable diameter instead of width/height */}
					{isCircularCutout ? (
						<InlineNumberInput
							category="cutout"
							label="Diameter"
							min={0.5}
							step={0.0625}
							value={skuCurrentItem.cutout.width}
							enabled={!readonly}
							onChange={(value) => {
								// Enable override mode to allow editing
								if (!panelItem.item.override) {
									panelItem.item.override = true;
								}
								// For circular cutouts, width = height = diameter
								panelItem.item.cutout = { width: value, height: value, depth: value };
								panelItem.item.size = { width: value, height: value, depth: value };
							}}
						/>
					) : (
						<>
							<InlineNumberInput
								category="cutout"
								label="Cutout Width"
								min={0.5}
								value={skuCurrentItem.cutout.width}
								enabled={false}
							/>
							<InlineNumberInput
								category="cutout"
								label="Cutout Height"
								min={0.5}
								value={skuCurrentItem.cutout.height}
								enabled={false}
							/>
						</>
					)}
					<InlineNumberInput
						category="cutout"
						label="Cutout Depth"
						min={0}
						value={skuCurrentItem.cutout.depth}
						enabled={false}
					/>
					{!isCircularCutout && (
						<>
							<InlineNumberInput
								category="appliance" label="Overall Width" min={0.5}
								value={skuCurrentItem.size.width}
								enabled={false}
							/>
							<InlineNumberInput
								category="appliance" label="Overall Height" min={0.5}
								value={skuCurrentItem.size.height}
								enabled={false}
							/>
							<InlineNumberInput
								category="appliance" label="Overall Depth" min={0}
								value={skuCurrentItem.size.depth}
								enabled={false}
							/>
						</>
					)}
					<InlineNumberInput
						category="appliance" label="Distance Below Counter" min={0}
						placeholder="NA"
						value={skuCurrentItem.distanceBelowCountertop}
						enabled={false}
					/>
					<InlineNumberInput
						category="appliance" label="Distance Front Of Face" min={0}
						value={ skuCurrentItem.distanceFrontOfFace }
						enabled={false}
					/>
				</Stack>
			</Box>

			{ controller.instanceOf === 'bp-controller' && (skuCurrentItem.placement === 'front only') &&
			<Box marginBottom={3} padding={2} textAlign="left"
				borderLeft="1px solid grey" borderTop="1px" roundedTopLeft="7px"
			>
				<Text paddingBottom={3} fontSize="medium">Other Settings</Text>
				<Stack spacing="3" width="100%" height="65%">
					<InlineNumberInput
						category="appliance" label="Side Padding" min={0}
						value={ panelItem.sidePadding ?? 0 }
						enabled={!readonly}
						onChange={(e) => {
							panelItem.sidePadding = e;
							// intDispatch({
							// 	type: BlueprintActionTypes.UpdatePanelItemSidePadding,
							// 	payload: {
							// 		pubState,
							// 		key: locator.ref.key,
							// 		sidePadding: panelItem.sidePadding
							// 	}
							// });
						}}
					/>
				</Stack>
			</Box>
			}

			{ controller.instanceOf === 'bp-controller' && (skuCurrentItem.dimensions.inside?.horizontal.render || skuCurrentItem.dimensions.inside?.vertical.render) && <>
				<Text pl="5px" color={dimensionHeaderColor} fontSize="medium"
					fontWeight="medium"
				>Dimension Positioning</Text>
				<Box marginBottom={3} padding={2} textAlign="left"
					borderLeft="1px solid grey" borderTop="1px" roundedTopLeft="7px"
				>
					<Stack spacing="3" width="100%" height="65%">
						{ skuCurrentItem.dimensions.inside.horizontal.render && <>
							<InlineSlider
								category="dimension" label="H. Line"
								min={0} max={1} step={0.01}
								value={ isCountertop ? panelItem.dimensionOffsets.zLine : panelItem.dimensionOffsets.hLine }
								enabled={!readonly}
								onChange={(e) => {
									panelItem.dimensionOffsets = isCountertop ? { zLine: e } : { hLine: e };
								}}
							/>
							<InlineSlider
								category="dimension" label="H. Label"
								min={0} max={1} step={0.01}
								value={ isCountertop ? panelItem.dimensionOffsets.zLabel : panelItem.dimensionOffsets.hLabel }
								enabled={!readonly}
								onChange={(e) => {
									panelItem.dimensionOffsets = isCountertop ? { zLabel: e } : { hLabel: e };
								}}
							/>
						</>}
						{ skuCurrentItem.dimensions.inside.horizontal.render && <>
							<InlineSlider
								category="dimension" label="V. Line"
								min={0} max={1} step={0.01}
								value={ panelItem.dimensionOffsets.vLine }
								enabled={!readonly}
								onChange={(e) => {
									panelItem.dimensionOffsets = { vLine: e };
								}}
							/>
							<InlineSlider
								category="dimension" label="V. Label"
								min={0} max={1} step={0.01}
								value={ panelItem.dimensionOffsets.vLabel }
								enabled={!readonly}
								onChange={(e) => {
									panelItem.dimensionOffsets = { vLabel: e };
								}}
							/>
						</>}
					</Stack>
				</Box>
			</>}
		</Stack>
	</ContextPanelContent>;
};
